import { Component, Input, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { SELECTOR_VALUES } from '../../../models/options';
import { AppService } from '../../../services/app.service';
import { UtilsService } from '../../../services/utils.service';
import { VirtualCardsService } from '../../../services/virtual_cards.service';

const CARD_TRANSACTION_OPERATIONS = [
  { code: 'TRANSACTION_APPROVED', name: 'Aprobada' },
  { code: 'TRANSACTION_REJECTED', name: 'Rechazada' },
  { code: 'TRANSACTION_REVERSED', name: 'Reversada' },
  { code: 'TRANSACTION_REFUND', name: 'Reembolso' },
  { code: 'WALLET_DEPOSIT', name: 'Depósito' },
  { code: 'OVERRIDE_VIRTUAL_BALANCE', name: 'Ajuste de saldo' },
  { code: 'MONTHLY_MAINTENANCE', name: 'Mantenimiento mensual' },
  { code: 'CARD_ISSUANCE_FEE', name: 'Emisión de tarjeta' },
];

@Component({
  selector: 'app-view-virtual-card-transactions',
  templateUrl: './view-virtual-card-transactions.component.html',
  styleUrl: './view-virtual-card-transactions.component.scss',
})
export class ViewVirtualCardTransactionsComponent implements OnInit {
  @Input() card: any = null;

  showTransactions = true;
  loading = false;

  transactions: any[] = [];
  total = 0;
  offset = 1;
  limit = 20;
  limitSelectorValues = SELECTOR_VALUES;
  limitSelected = this.limitSelectorValues[0];

  fromDate = '';
  toDate = '';
  operationOptions = CARD_TRANSACTION_OPERATIONS;
  selectedOperations = CARD_TRANSACTION_OPERATIONS.map((item) => item.code);

  constructor(
    private cardsService: VirtualCardsService,
    public utilsService: UtilsService,
    private messageService: MessageService,
    public appService: AppService,
  ) {}

  ngOnInit() {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - 90);
    this.toDate = this.toInputDate(today);
    this.fromDate = this.toInputDate(from);
    this.loadTransactions();
  }

  exitView() {
    this.appService.triggerViewVirtualCardTransactions(false);
  }

  refresh() {
    if (this.loading) return;
    this.offset = 1;
    this.loadTransactions();
  }

  nextPage() {
    if (!this.atLastPage() && !this.loading) {
      this.offset++;
      this.loadTransactions();
    }
  }

  previousPage() {
    if (this.offset > 1 && !this.loading) {
      this.offset--;
      this.loadTransactions();
    }
  }

  atLastPage(): boolean {
    return this.offset * this.limit >= this.total;
  }

  changeLimit(event: any) {
    this.limit = event.value.code;
    this.refresh();
  }

  loadTransactions() {
    if (!this.card?.id || this.loading) return;

    this.loading = true;
    this.cardsService
      .getCardTransactions(this.card.id, this.offset, this.limit, {
        from_date: this.fromDate || null,
        to_date: this.toDate || null,
        operations: this.selectedOperations?.length
          ? this.selectedOperations
          : null,
      })
      .then((resp: any) => {
        this.loading = false;
        this.transactions = Array.isArray(resp?.data) ? resp.data : [];
        this.total = Number(resp?.total_records || 0);
      })
      .catch((error: any) => {
        this.loading = false;
        this.transactions = [];
        this.total = 0;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            error?.error ||
            error?.message ||
            'Ocurrió un error al cargar las transacciones de la tarjeta.',
        });
      });
  }

  operationLabel(movement: any): string {
    const code = this.operationCode(movement);
    return (
      this.operationOptions.find((item) => item.code === code)?.name ||
      code ||
      '—'
    );
  }

  operationCode(movement: any): string {
    return String(
      movement?.operation || movement?.type || movement?.status || '',
    ).toUpperCase();
  }

  operationStatusClass(movement: any): string {
    const code = this.operationCode(movement);
    if (
      code === 'TRANSACTION_APPROVED' ||
      code === 'WALLET_DEPOSIT' ||
      code === 'MONTHLY_MAINTENANCE' ||
      code === 'CARD_ISSUANCE_FEE'
    ) {
      return 'finished';
    }
    if (code === 'TRANSACTION_REJECTED') {
      return 'cancelled';
    }
    if (
      code === 'TRANSACTION_REVERSED' ||
      code === 'TRANSACTION_REFUND' ||
      code === 'OVERRIDE_VIRTUAL_BALANCE'
    ) {
      return 'processing';
    }
    return '';
  }

  movementDate(movement: any): string {
    return (
      movement?.date ||
      movement?.created_at ||
      movement?.datetime ||
      movement?.timestamp ||
      movement?.operation_date ||
      movement?.transaction_date ||
      ''
    );
  }

  merchantName(movement: any): string {
    return (
      movement?.merchant_data?.name ||
      movement?.merchant_name ||
      movement?.merchant?.name ||
      movement?.description ||
      '—'
    );
  }

  movementAmount(movement: any): number | null {
    const value = [
      movement?.amount,
      movement?.billing_amount,
      movement?.transaction_amount,
      movement?.gross_amount,
      movement?.bill_amount,
      movement?.local_amount,
      movement?.new_balance,
    ].find((item) => {
      const amount = Number(item);
      return Number.isFinite(amount) && amount !== 0;
    });
    if (value == null) {
      return null;
    }
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
  }

  movementCurrency(movement: any): string {
    return (
      movement?.currency ||
      movement?.billing_currency ||
      movement?.transaction_currency ||
      this.card?.currency ||
      'USD'
    );
  }

  declinedReason(movement: any): string {
    const reason = movement?.declined_reason;
    if (!reason) return '';
    if (typeof reason === 'string') return reason;
    return (
      reason.description ||
      reason.message ||
      reason.code ||
      ''
    );
  }

  private toInputDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
